import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslationApp } from "../../../lib/i18next";
import ModalMedia from "../ModalMedia";
import {
  LOADING_CONTENT,
  MODAL_CREATE_WITH_IA_META_AD_LIBRARY_LOADING,
  MODAL_GALLERY_EMPTY_CONTENT,
  MODAL_GALLERY_META_AD_LIBRARY_EMPTY_CONTENT,
} from "../../../i18n/keysTranslations";
import {
  getReferenceAdStoragePath,
  IMAGE_FORMATS,
} from "../../../utils/constants";
import { getPathDatabaseByRole } from "../../../actions/auth";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useRebrandBanner } from "../../../hooks/useRebrandBanner";

import {
  MODAL_GALLERY_FETCH_MODE_META_AD_LIBRARY,
  MODAL_GALLERY_FETCH_MODE_URL,
} from "./constants";
import { useGalleryFromUrlSelection } from "./useGalleryFromUrlSelection";
import { useGalleryFromWebsiteUrl } from "./useGalleryFromWebsiteUrl";
import { useGalleryFromMetaAdLibrary } from "./useGalleryFromMetaAdLibrary";
import ModalGalleryFromUrlLayout from "./ModalGalleryFromUrlLayout";

const ModalGalleryFromUrl = ({
  modalOpen,
  onCloseModal,
  onCallbackSelectedItems,
  fetchUrl,
  fetchMode = MODAL_GALLERY_FETCH_MODE_URL,
  multiple = true,
  maxSelections = null,
  acceptedFormats = IMAGE_FORMATS,
  minDimensions = {},
  titleKey,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const pathStorageReferenceAd = getReferenceAdStoragePath(userID);
  // Same path as reference-image uploads in CreateWithIA (not gallery).
  const pathStorageWebsiteImages = userID ? `socialMedia/${userID}/` : "";

  const isMetaAdLibraryMode =
    fetchMode === MODAL_GALLERY_FETCH_MODE_META_AD_LIBRARY;

  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [businessCompetitors, setBusinessCompetitors] = useState([]);
  const [selectPreview, setSelectPreview] = useState(null);

  const isPlanActive = useSelectorApp((state) => state?.payments?.isPlanActive);
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );
  const { totalTopOffsetPx } = useRebrandBanner();

  const websiteUrl = useGalleryFromWebsiteUrl({
    dispatch,
    fetchUrl,
    isPlanActive,
    isTrial,
    pathStorage: pathStorageWebsiteImages,
    setData,
    setError,
    setIsLoading,
    setIsUploading,
  });

  const metaAdLibrary = useGalleryFromMetaAdLibrary({
    dispatch,
    pathStorageReferenceAd,
    businessCompetitors,
    setData,
    setError,
    setIsLoading,
    setIsUploading,
    setBusinessCompetitors,
    data,
  });

  const galleryDisplayData = isMetaAdLibraryMode
    ? metaAdLibrary.galleryDisplayData
    : data;

  const {
    resetSelection,
    onSelectItem,
    selectedGalleryIndexes,
    hasSelection,
    getSelectedItems,
    galleryMultiple,
    usePickerMode,
    setSelectedItems,
  } = useGalleryFromUrlSelection({
    galleryDisplayData,
    multiple,
    maxSelections,
  });

  const onGetInitialData = useCallback(async () => {
    resetSelection();
    setSelectPreview(null);
    if (isMetaAdLibraryMode) {
      await metaAdLibrary.onGetInitialData();
    } else {
      await websiteUrl.onGetInitialData();
    }
    //eslint-disable-next-line
  }, [
    isMetaAdLibraryMode,
    metaAdLibrary.onGetInitialData,
    resetSelection,
    websiteUrl.onGetInitialData,
  ]);

  const onSelectPreview = useCallback((indexOrData) => {
    setSelectPreview(indexOrData);
  }, []);

  const previewItem = useMemo(() => {
    if (selectPreview === null) return null;

    const previewIndex =
      typeof selectPreview === "object" && selectPreview !== null
        ? selectPreview.index
        : selectPreview;

    return galleryDisplayData[previewIndex] ?? null;
  }, [galleryDisplayData, selectPreview]);

  useEffect(() => {
    if (!modalOpen) {
      setSelectPreview(null);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (!isMetaAdLibraryMode) return;
    resetSelection();
    setSelectPreview(null);
  }, [
    isMetaAdLibraryMode,
    metaAdLibrary.selectedCompetitorFilter,
    resetSelection,
  ]);

  const onFailedLoadUrl = (index) => {
    if (isMetaAdLibraryMode) return;
    websiteUrl.onFailedLoadUrl(index, setSelectedItems);
  };

  const onSendSelectedItems = useCallback(async () => {
    const itemsToProcess = getSelectedItems();
    if (!itemsToProcess.length) return;

    if (isMetaAdLibraryMode) {
      await metaAdLibrary.onSendSelectedItems(itemsToProcess, {
        onCallbackSelectedItems,
        onCloseModal,
      });
      return;
    }

    await websiteUrl.onSendSelectedItems(itemsToProcess, {
      onCallbackSelectedItems,
      onCloseModal,
    });
  }, [
    getSelectedItems,
    isMetaAdLibraryMode,
    metaAdLibrary,
    onCallbackSelectedItems,
    onCloseModal,
    websiteUrl,
  ]);

  useEffect(() => {
    if (!modalOpen) return;
    onGetInitialData();
  }, [modalOpen, onGetInitialData]);

  const emptyContentKey = isMetaAdLibraryMode
    ? MODAL_GALLERY_META_AD_LIBRARY_EMPTY_CONTENT
    : MODAL_GALLERY_EMPTY_CONTENT;

  const loadingMessageKey = isMetaAdLibraryMode
    ? MODAL_CREATE_WITH_IA_META_AD_LIBRARY_LOADING
    : LOADING_CONTENT;

  return (
    <>
      <ModalGalleryFromUrlLayout
        t={t}
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        titleKey={titleKey}
        isLoading={isLoading}
        loadingMessageKey={loadingMessageKey}
        error={error}
        totalTopOffsetPx={totalTopOffsetPx}
        emptyContentKey={emptyContentKey}
        isMetaAdLibraryMode={isMetaAdLibraryMode}
        showCompetitorFilter={
          isMetaAdLibraryMode && !isLoading && !error && data.length > 0
        }
        competitorFilterControl={metaAdLibrary.control}
        competitorFilterOptions={metaAdLibrary.competitorFilterOptions}
        platformFilterOptions={metaAdLibrary.platformFilterOptions}
        onPlatformFilterChange={metaAdLibrary.onPlatformFilterChange}
        galleryDisplayData={galleryDisplayData}
        hasSelection={hasSelection}
        isUploading={isUploading}
        showUploadingFooter={!isMetaAdLibraryMode}
        onConfirmSelection={onSendSelectedItems}
        galleryProps={{
          data: galleryDisplayData,
          onCloseModal,
          lastItemRef: undefined,
          loadLastItem: true,
          onFailedLoadUrl,
          onSelectItem,
          selectedItems: selectedGalleryIndexes,
          onSelectPreview: isMetaAdLibraryMode ? onSelectPreview : () => {},
          multiple: galleryMultiple,
          overlayPickerMode: usePickerMode,
          selectedItemsArray: usePickerMode ? selectedGalleryIndexes : [],
          showActionButtons: isMetaAdLibraryMode,
          showPreviewExpandOnly: isMetaAdLibraryMode,
          showEditButton: isMetaAdLibraryMode ? false : undefined,
          showDeleteButton: !isMetaAdLibraryMode,
          showSourceSelector: false,
          showFiltersSelector: false,
          showRating: false,
          showMetaAdDuration: isMetaAdLibraryMode,
          from: isMetaAdLibraryMode
            ? MODAL_GALLERY_FETCH_MODE_META_AD_LIBRARY
            : undefined,
          acceptedFormats: isMetaAdLibraryMode ? [] : acceptedFormats,
          minDimensions: isMetaAdLibraryMode ? {} : minDimensions,
        }}
      />

      {isMetaAdLibraryMode && previewItem ? (
        <ModalMedia
          modalOpen={selectPreview !== null}
          onCloseModal={() => setSelectPreview(null)}
          url={previewItem.url}
          item={previewItem}
          showEditButton={false}
          showShareButton={false}
          showDeleteButton={false}
          showDownloadButton={false}
        />
      ) : null}
    </>
  );
};

export default ModalGalleryFromUrl;
